import { TradeBlotterType } from '@oneview/constants/lookups';
import type { RootState } from '@oneview/redux/rootReducer';
import type { AccountSettings, ModelSettings } from '@oneview/types/settings';
import type { ApiPermissions } from '@orbis-systems/orbis-api-middleware';
import { createSelector } from '@reduxjs/toolkit';

export const selectAllSettings = (state: RootState) => state.setting;

export const selectGlobalSettings = createSelector(selectAllSettings, (settings) => settings.common.global);

export const selectAdvisorSettings = createSelector(selectAllSettings, (settings) => settings.advisor);

export const selectClientSettings = createSelector(selectAllSettings, (settings) => settings.client);

export const selectTableSettings = createSelector(selectAllSettings, (settings) => settings.table);

export const selectTradeBlotters = createSelector(
    selectAllSettings,
    (_: RootState, props?: { type?: TradeBlotterType }) => props?.type,
    (settings, type) => {
        const blotters = Object.values(settings.tradeBlotter);

        if (type) {
            return blotters.filter((item) => item.type === type);
        }

        return blotters;
    },
);

export const selectUserProfileSettings = createSelector(selectAllSettings, (settings) => {
    return settings.advisor.userProfile;
});

export const selectModelSettings = createSelector(
    selectAllSettings,
    (_, props?: { modelId?: number }) => props?.modelId || 0,
    (settings, modelId) => {
        return (settings.model[modelId] || null) as ModelSettings | null;
    },
);

export const selectAccountSettings = createSelector(
    selectAllSettings,
    (_, props?: { accountId?: number }) => props?.accountId ?? 0,
    (settings, accountId) => {
        return (settings.account[accountId] || null) as AccountSettings | null;
    },
);

export const selectMiddlewareSettings = createSelector(selectAllSettings, (settings) => settings.middleware);

export const selectRequireNbbo = createSelector(selectMiddlewareSettings, (settings) => settings.RequireNbbo);

export const selectApiPermissions = createSelector(
    selectMiddlewareSettings,
    ({ AddIavProcessToken, AllowRtbs, AllowIav, AllowTransfers, AllowDocuments }) => {
        const apiPermissions: Partial<ApiPermissions> = {
            rtbs: AllowRtbs,
            iav: AllowIav,
            transfers: AllowTransfers,
            documents: AllowDocuments,
            iavProcessorToken: AddIavProcessToken,
        };

        return apiPermissions;
    },
);
